import { useEffect, useState } from 'react';
import { useSession } from '../auth/session.tsx';
import type { DevUser } from '../api/types.ts';

/**
 * The dev picker exists only when the server said mode: 'local'. There is no
 * build-time flag that could carry it into Azure — the API refuses to be in
 * local mode unless ENVIRONMENT=local and the database is on this machine,
 * and main.bicep pins AUTH_MODE=entra.
 */
export function SignIn() {
  const { config, signIn, signInReason } = useSession();
  const [users, setUsers] = useState<DevUser[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Said on the sign-in screen, because that is where a refused session lands.
  // Without it a server that refuses every token looks like a sign-in button
  // that does nothing.
  const refused = signInReason === 'rejected' && (
    <div role="alert" className="mt-4 rounded-lg border-l-8 border-critical-edge bg-critical-bg
                                 px-4 py-3 text-critical-fg">
      <p className="font-semibold">The server did not accept your session.</p>
      <p className="mt-1 text-[0.9375rem]">
        You have been signed out. If this happens again straight after signing in,
        signing in again will not fix it: the server is refusing the sign-in, and
        its log says why.
      </p>
    </div>
  );

  useEffect(() => {
    if (config.mode !== 'local') return;
    void fetch('/api/dev/users')
      .then((r) => r.json())
      .then((d: { users: DevUser[] }) => setUsers(d.users))
      .catch(() => setError('Could not load the local staff list.'));
  }, [config.mode]);

  if (config.mode === 'entra') {
    return (
      <div className="mx-auto flex min-h-dvh max-w-sm flex-col justify-center p-6">
        <h1 className="text-2xl font-bold lowercase">procura</h1>
        <p className="mt-2 text-neutral-600">Sign in with your work account.</p>
        {refused}
        <button
          type="button"
          onClick={() => void signIn()}
          className="mt-6 min-h-[52px] rounded-lg bg-neutral-900 px-4 font-semibold text-white
                     active:bg-neutral-700"
        >
          Sign in with Microsoft
        </button>
      </div>
    );
  }

  return (
    <div className="mx-auto min-h-dvh max-w-lg p-6">
      <h1 className="text-2xl font-bold lowercase">procura</h1>
      <div className="mt-3 rounded-lg border-l-8 border-warning-edge bg-warning-bg
                      px-4 py-3 text-warning-fg">
        <p className="text-xs font-bold tracking-[0.14em] uppercase">Local development</p>
        <p className="mt-1 text-[0.9375rem]">
          Signing in as anyone, without a password. This picker does not exist
          when the server runs in Entra mode.
        </p>
      </div>

      {refused}
      {error && <p className="mt-4 text-critical-edge">{error}</p>}

      <ul className="mt-5 space-y-2">
        {users.map((u) => (
          <li key={u.email}>
            <button
              type="button"
              disabled={busy !== null}
              onClick={() => {
                setBusy(u.email);
                setError(null);
                void signIn(u.email)
                  .catch((e: Error) => setError(e.message))
                  .finally(() => setBusy(null));
              }}
              className="flex min-h-[60px] w-full items-center gap-3 rounded-lg border
                         border-neutral-200 bg-white px-4 py-2.5 text-left
                         active:bg-neutral-100 disabled:opacity-50"
            >
              <span className="min-w-0">
                <span className="block font-semibold text-neutral-900">{u.full_name}</span>
                <span className="block text-sm text-neutral-500">{u.role_name}</span>
              </span>
              <span className="ml-auto shrink-0 text-sm text-neutral-400">
                tier {u.tier}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
